/* eslint-disable max-lines-per-function, max-lines */
import {
  useArtworkForm,
  useArtworkImage,
  type UploadArtworkParams
} from "@design-lab/entities/artwork";
import {
  useCompositionForm,
  useCompositionsActions,
  useCurrentComposition
} from "@design-lab/entities/composition";
import {
  formToApiPayload,
  mapMadeDesignProjectToGetDesignProjectResponse
} from "@design-lab/entities/composition/lib/design-project";
import { pinningSessionsAtom } from "@design-lab/entities/pinning";
import { useFetchPrintOnDemandProduct } from "@design-lab/entities/product";
import { useQuery } from "@design-lab/shared/graphql";
import { useDebounce } from "@design-lab/shared/hooks/useDebounce";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useIsSample } from "@design-lab/shared/lib/config";
import type { DesignLabFormType } from "@design-lab/shared/types";
import { GetDesignProjectDocument } from "graphql-api/operations";
import type { models } from "jiffy-api";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { useParams } from "react-router";
import { useMadeEdit } from "../api/useMadeEdit";
import { useDesignLabInit } from "./hooks/useDesignLabInit";
import { useFormRestoration } from "./hooks/useFormRestoration";
import { useUpdateWithDeliveryEstimates } from "./useUpdateWithDeliveryEstimates";

const DEBOUNCE_DELAY = 2000;

function useDesignProjectSyncCreator() {
  const { form: artworkForm } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const { currentProduct } = useCurrentComposition();
  const { addComposition } = useCompositionsActions();
  const fetchProduct = useFetchPrintOnDemandProduct();
  const { uploadArtwork } = useArtworkImage();
  const updateProject = useUpdateWithDeliveryEstimates();
  const { isEditMode, compositionId, assemblyGroupId } = useEditMode();
  const isSample = useIsSample();
  const { uuid } = useParams<{ uuid?: string }>();
  const hasRestoredRef = useRef(false);
  const restorationInProgressRef = useRef(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const hasProcessedFilesRef = useRef(false);
  // Auto-save tracking
  const projectIdRef = useRef<string | null>(null);

  // Use the new simplified init machine
  const {
    isEditMode: isEditModeFromInit,
    isReady,
    isLoading,
    project,
    latestProject,
    uploadSource,
    uploadedFiles,
    externalUploads,
    targetSide,
    showModal,
    composition,
    error,
    isWaitingForLibrary,
    isWaitingForCanva,
    handleLibrarySelection,
    handleCanvaComplete,
    continueWithLatest,
    startNew,
    closeModal,
    selectFiles,
    retry,
    reset
  } = useDesignLabInit();

  // Project is already loaded by the init machine, no need for additional queries
  const effectiveUuid = project?.uuid || uuid;
  const effectiveEditMode = isEditMode || isEditModeFromInit;

  // Load edit mode project data
  const {
    data: editModeProject,
    isLoading: isLoadingEditMode,
    error: editModeError
  } = useMadeEdit(effectiveEditMode ? assemblyGroupId || undefined : undefined);

  // Only query cache if we don't have project from init machine
  // TODO: But project from init machine does not have details like name, email, phone number so removing !!project check
  const { data: cachedProjectData } = useQuery(GetDesignProjectDocument, {
    skip: !effectiveUuid || effectiveEditMode,
    fetchPolicy: "cache-first",
    variables: {
      sample: isSample,
      uuid: effectiveUuid,
      new: false
    }
  });

  const loadedProjectData = useMemo(() => {
    // Prefer project from init machine
    if (project) {
      return project;
    }
    // In edit mode, use edit mode project data
    // Cast to getLatestDesignProject.Response since useFormRestoration expects this type
    // The structures are compatible - both have id, uuid, quantities, placements
    if (effectiveEditMode && editModeProject) {
      return editModeProject as models.designLab.getLatestDesignProject.Response;
    }
    // Fallback to Apollo cache (for backward compatibility)
    if (cachedProjectData?.madeDesignProject) {
      return mapMadeDesignProjectToGetDesignProjectResponse(cachedProjectData.madeDesignProject);
    }
    return undefined;
  }, [project, editModeProject, effectiveEditMode, cachedProjectData]);

  // Form restoration - use loaded project data (full GraphQL format) for restoration
  // Note: productsMap is optional - form restoration works without it
  // currentProduct comes from useCurrentComposition which loads products automatically
  const { restoreFromProject, lastSavedDataRef: restorationLastSavedRef } = useFormRestoration({
    projectData: (loadedProjectData as any) || null,
    currentProduct,
    compositionId,
    productsMap: new Map(), // Empty map - enhancement is optional
    shouldClearTargetSide:
      uploadSource === "library" || uploadSource === "canva" || uploadSource === "external",
    targetSide: targetSide || "front",
    preserveExistingArtwork: false
  });

  // Helper to apply composition from URL to form
  const applyCompositionToForm = useCallback(
    async (urlComposition: typeof composition) => {
      if (!urlComposition) return;

      try {
        // Fetch the product to get the composition details
        const product = await fetchProduct(urlComposition.catalogNumber);
        if (!product || !product.compositions) return;

        // Find the composition that matches the color (ac)
        const matchingComposition = product.compositions.find(
          comp => comp.colorName === urlComposition.ac || comp.id === urlComposition.ac
        );

        if (!matchingComposition) {
          return;
        }

        // Add the composition using the proper structure
        const initialVariants: Record<string, number> = {};
        if (urlComposition.pdpSizeData) {
          Object.entries(urlComposition.pdpSizeData).forEach(([variantId, data]) => {
            const quantity = (data as any).quantity || data;
            if (quantity > 0) {
              initialVariants[variantId] = Number(quantity);
            }
          });
        }

        // In sample mode, replace existing compositions instead of appending
        // Sample mode only allows 1 product per project
        if (isSample) {
          compositionForm.setValue("addedCompositions", []);
        }

        addComposition({
          catalogId: urlComposition.catalogNumber,
          colorName: matchingComposition.colorName,
          compositionId: matchingComposition.id,
          initialVariants
        });
      } catch {
        // Silently handle composition application errors
      }
    },
    [fetchProduct, addComposition, isSample, compositionForm]
  );

  // Reset restoration state when project UUID changes or edit mode changes
  useEffect(() => {
    if ((uuid && uuid !== projectIdRef.current) || (effectiveEditMode && assemblyGroupId)) {
      hasRestoredRef.current = false;
      restorationInProgressRef.current = false;
      setIsRestoring(false);
    }
  }, [uuid, effectiveEditMode, assemblyGroupId]);

  // Hydrate pinning sessions from loaded project data
  const setPinningSessions = useSetAtom(pinningSessionsAtom);
  useEffect(() => {
    if (!loadedProjectData) return;

    const placementsData = loadedProjectData?.placements;
    const placements = [
      placementsData?.front,
      placementsData?.back,
      placementsData?.frontLeftChest
    ].filter(Boolean);

    for (const placement of placements) {
      const artwork = placement?.dtfArtwork;
      const session = artwork?.pinSession;
      // eslint-disable-next-line no-continue
      if (!artwork?.id || !session) continue;

      const artworkId = String(artwork.id);
      setPinningSessions(prev => ({
        ...prev,
        [artworkId]: {
          sessionId: session.id,
          state: session.state,
          summary: session.summary ?? null
        }
      }));
    }
  }, [loadedProjectData, setPinningSessions]);

  // Restore form when project is loaded
  useEffect(() => {
    // Don't restore if modal is showing - wait for user choice
    if (showModal) {
      return;
    }

    // In edit mode, wait for edit mode project to load
    const isEditModeLoading = effectiveEditMode && isLoadingEditMode;
    const shouldRestore =
      isReady || (loadedProjectData && uploadSource) || (effectiveEditMode && loadedProjectData);

    if (
      loadedProjectData &&
      !hasRestoredRef.current &&
      !restorationInProgressRef.current &&
      shouldRestore &&
      !isEditModeLoading
    ) {
      restorationInProgressRef.current = true;
      setIsRestoring(true);

      restoreFromProject()
        .then(async () => {
          hasRestoredRef.current = true;
          restorationInProgressRef.current = false;
          setIsRestoring(false);

          // After restoration, apply composition from URL if present
          if (composition) {
            await applyCompositionToForm(composition);
          }
        })
        .catch(() => {
          hasRestoredRef.current = false;
          restorationInProgressRef.current = false;
          setIsRestoring(false);
        });
    } else if (
      !loadedProjectData &&
      shouldRestore &&
      composition &&
      !hasRestoredRef.current &&
      !effectiveEditMode
    ) {
      // New project with composition params - apply them directly (not in edit mode)
      applyCompositionToForm(composition).then(() => {
        hasRestoredRef.current = true;
      });
    }
  }, [
    loadedProjectData,
    restoreFromProject,
    compositionForm,
    isReady,
    uploadSource,
    showModal,
    composition,
    applyCompositionToForm,
    effectiveEditMode,
    isLoadingEditMode
  ]);

  // Handle files downloaded from external URLs
  useEffect(() => {
    if (uploadedFiles && uploadedFiles.length > 0 && !hasProcessedFilesRef.current) {
      hasProcessedFilesRef.current = true;

      // Upload the first file to the specified side
      const file = uploadedFiles[0];
      if (file) {
        const uploadParams: UploadArtworkParams = {
          file,
          side: targetSide || "front"
        };
        const existingArtworkId = externalUploads?.[0]?.id;
        if (existingArtworkId) {
          uploadParams.existingArtworkId = existingArtworkId;
        }

        uploadArtwork(uploadParams);
      }
    }
  }, [uploadedFiles, uploadArtwork, targetSide, externalUploads]);

  // Reset the flag when files change
  useEffect(() => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      hasProcessedFilesRef.current = false;
    }
  }, [uploadedFiles]);

  // Watch both forms and combine their values
  const artworkFormValues = useWatch({ control: artworkForm.control });
  const compositionFormValues = useWatch({ control: compositionForm.control });
  const formValues = { artwork: artworkFormValues, compositions: compositionFormValues };
  const debouncedFormValues = useDebounce(formValues, DEBOUNCE_DELAY);

  // Use the lastSavedDataRef from form restoration so we're comparing against the restored state
  const lastSavedDataRef = restorationLastSavedRef;

  // Update project ID ref when available (use orchestrator UUID first, then URL UUID)
  useEffect(() => {
    const finalProjectUuid = project?.uuid || uuid;
    if (finalProjectUuid && finalProjectUuid !== projectIdRef.current) {
      projectIdRef.current = finalProjectUuid;
      artworkForm.setValue("designProjectUuid", finalProjectUuid);
    }
  }, [project?.uuid, uuid, artworkForm]);

  // Auto-save logic
  const canSave = useCallback(() => {
    // Don't save during initialization or if not ready
    if (!isReady || isLoading) {
      return false;
    }

    // Don't save if we don't have a project ID or form data
    if (!projectIdRef.current || !debouncedFormValues) {
      return false;
    }

    const formData = debouncedFormValues as Partial<DesignLabFormType>;
    if (!formData.compositions?.currentCompositionId) {
      return false;
    }

    const { artwork } = formData;
    if (artwork) {
      // Only validate placements that will actually be sent to the API
      // For front: only the selected placement (front or frontLeftChest)
      // For back: always validate
      const isLeftChest = artwork.selectedFrontPlacement === "frontLeftChest";
      const activeFrontPlacement = isLeftChest ? artwork.frontLeftChest : artwork.front;
      const placementsToValidate = [activeFrontPlacement, artwork.back].filter(Boolean);

      const hasInvalidPlacement = placementsToValidate.some(
        placement =>
          placement?.artworkId &&
          placement?.size &&
          (placement.size.width <= 0 || placement.size.height <= 0)
      );

      if (hasInvalidPlacement) {
        return false;
      }
    }

    return true;
  }, [isReady, isLoading, debouncedFormValues]);

  // Auto-save effect
  useEffect(() => {
    if (!canSave()) {
      return;
    }

    const formData = debouncedFormValues as DesignLabFormType;
    const payload = formToApiPayload(formData);
    const payloadString = JSON.stringify(payload);

    // Don't save if nothing changed
    if (payloadString === lastSavedDataRef.current) {
      return;
    }

    lastSavedDataRef.current = payloadString;

    if (projectIdRef.current) {
      updateProject.mutate({
        id: projectIdRef.current,
        payload
      });
    }
  }, [canSave, debouncedFormValues, updateProject, lastSavedDataRef]);

  // Manual save function
  const saveProject = useCallback(
    (force = false) => {
      if (!canSave() && !force) {
        return;
      }

      const formData = debouncedFormValues as DesignLabFormType;
      const payload = formToApiPayload(formData);

      if (projectIdRef.current) {
        updateProject.mutate({
          id: projectIdRef.current,
          payload
        });
      }
    },
    [canSave, debouncedFormValues, updateProject]
  );

  // Memoize return object to prevent unnecessary re-renders of all consumers
  return useMemo(
    () => ({
      // Initialization state
      stage: (() => {
        if (isReady) return "ready";
        if (isLoading || (effectiveEditMode && isLoadingEditMode)) return "loading";
        return "initializing";
      })(),
      isInitialized: isReady || (effectiveEditMode && !!loadedProjectData && !isLoadingEditMode),
      isLoading: isLoading || (effectiveEditMode && isLoadingEditMode),
      isRestoring,
      initError: error || (effectiveEditMode ? editModeError : undefined),

      // Project data
      projectUuid: project?.uuid || effectiveUuid,
      projectData: loadedProjectData || null,
      // TODO: Remove after updating save project to use the new project format
      // Need this for backward compatibility with the save project UI
      graphqlProjectData: cachedProjectData?.madeDesignProject || null,
      latestProject,

      // Workflow state
      activeWorkflow: uploadSource || "none",
      isProcessingWorkflow: !!uploadSource,

      // Modal controls
      isDesignLibraryOpen: uploadSource === "library" || isWaitingForLibrary,
      isCanvaOpen: uploadSource === "canva" || isWaitingForCanva,
      isAiDesignOpen: false, // Not supported in new flow
      showModal,
      isWaitingForLibrary,
      isWaitingForCanva,
      uploadSource,
      targetSide,

      // Actions
      retryInit: retry,
      reset,
      saveProject,
      handleLibrarySelection,
      handleCanvaComplete,
      continueWithLatest,
      startNew,
      closeModal,
      selectFiles,

      // Auto-save tracking
      lastSavedDataRef,

      // Update mutation for external use
      updateProject
    }),
    [
      isReady,
      isLoading,
      error,
      project?.uuid,
      effectiveUuid,
      loadedProjectData,
      latestProject,
      uploadSource,
      showModal,
      isWaitingForLibrary,
      isWaitingForCanva,
      targetSide,
      handleLibrarySelection,
      handleCanvaComplete,
      retry,
      reset,
      saveProject,
      continueWithLatest,
      startNew,
      closeModal,
      selectFiles,
      updateProject,
      lastSavedDataRef,
      isRestoring,
      effectiveEditMode,
      isLoadingEditMode,
      editModeError,
      cachedProjectData
    ]
  );
}

type DesignProjectSyncContextValue = ReturnType<typeof useDesignProjectSyncCreator>;

const designProjectSyncAtom = atom<DesignProjectSyncContextValue | null>(null);

export function DesignProjectSyncProvider({ children }: PropsWithChildren) {
  const value = useDesignProjectSyncCreator();
  useHydrateAtoms([[designProjectSyncAtom, value]]);
  const setDesignProjectSync = useSetAtom(designProjectSyncAtom);
  useEffect(() => {
    setDesignProjectSync(value);
  }, [setDesignProjectSync, value]);
  return children;
}

export function useDesignProjectSync() {
  const value = useAtomValue(designProjectSyncAtom);
  if (!value) {
    throw new Error("DesignProjectSyncProvider is missing");
  }
  return value;
}
